import { createSlice, createEntityAdapter, createSelector, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'src/Common/RTK/store';
import type { SystemPrompt, PromptCategory } from '../types/promptGallery.types';
import { promptGalleryApi } from './promptGalleryApi';

/**
 * Entity adapter for normalized prompt storage
 * Sorts prompts by sortOrder for consistent display
 */
const promptsAdapter = createEntityAdapter<SystemPrompt>({
    selectId: (prompt) => prompt.id,
    sortComparer: (a, b) => a.sortOrder - b.sortOrder,
});

/**
 * UI state for Prompt Gallery
 */
interface PromptGalleryUIState {
    /** Whether the gallery modal is open */
    isOpen: boolean;
    /** Current filter selection */
    filter: PromptCategory | 'All';
    /** ID of currently selected prompt (for preview/details) */
    selectedPromptId: string | null;
}

/**
 * Complete state shape
 */
type PromptGalleryState = ReturnType<typeof promptsAdapter.getInitialState> & {
    ui: PromptGalleryUIState;
};

/**
 * Initial state
 */
const initialState: PromptGalleryState = promptsAdapter.getInitialState({
    ui: {
        isOpen: false,
        filter: 'All' as const,
        selectedPromptId: null,
    },
});

/**
 * Prompt Gallery Redux slice
 * Manages prompt data and gallery UI state
 */
export const promptGallerySlice = createSlice({
    name: 'promptGallery',
    initialState,
    reducers: {
        /**
         * Open the gallery modal
         */
        openGallery: (state) => {
            state.ui.isOpen = true;
        },
        /**
         * Close the gallery modal
         */
        closeGallery: (state) => {
            state.ui.isOpen = false;
            state.ui.selectedPromptId = null; // Clear selection on close
        },
        /**
         * Set the category filter
         */
        setFilter: (state, action: PayloadAction<PromptCategory | 'All'>) => {
            state.ui.filter = action.payload;
        },
        /**
         * Select a prompt for preview/details
         */
        selectPrompt: (state, action: PayloadAction<string | null>) => {
            state.ui.selectedPromptId = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Populate entity adapter when API call succeeds
        builder.addMatcher(
            promptGalleryApi.endpoints.getPrompts.matchFulfilled,
            (state, action) => {
                promptsAdapter.setAll(state, action.payload.prompts);
            }
        );
    },
});

/**
 * Action creators
 */
export const {
    openGallery,
    closeGallery,
    setFilter,
    selectPrompt,
} = promptGallerySlice.actions;

/**
 * Reducer export
 */
export const promptGalleryReducer = promptGallerySlice.reducer;

/**
 * Entity adapter selectors
 */
const adapterSelectors = promptsAdapter.getSelectors<RootState>(
    (state) => state.promptGallery
);

/**
 * Basic selectors
 */
export const selectAllPrompts = adapterSelectors.selectAll;
export const selectPromptById = adapterSelectors.selectById;
export const selectPromptIds = adapterSelectors.selectIds;
export const selectPromptEntities = adapterSelectors.selectEntities;
export const selectTotalPrompts = adapterSelectors.selectTotal;

/**
 * UI state selectors
 */
export const selectIsGalleryOpen = (state: RootState) => state.promptGallery.ui.isOpen;
export const selectCurrentFilter = (state: RootState) => state.promptGallery.ui.filter;
export const selectSelectedPromptId = (state: RootState) => state.promptGallery.ui.selectedPromptId;

/**
 * Memoized selector for filtered prompts
 * Filters prompts based on current category filter
 */
export const selectFilteredPrompts = createSelector(
    [selectAllPrompts, selectCurrentFilter],
    (prompts, filter) => {
        if (filter === 'All') {
            return prompts;
        }
        return prompts.filter(prompt => prompt.category === filter);
    }
);

/**
 * Memoized selector for currently selected prompt
 */
export const selectSelectedPrompt = createSelector(
    [selectPromptEntities, selectSelectedPromptId],
    (entities, selectedId) => {
        if (!selectedId) return null;
        return entities[selectedId] || null;
    }
);

/**
 * Memoized selector for prompts grouped by category
 */
export const selectPromptsByCategory = createSelector(
    [selectAllPrompts],
    (prompts) => {
        const grouped: Record<PromptCategory, SystemPrompt[]> = {
            Search: [],
            Summarize: [],
            Analyze: [],
            Track: [],
        };

        prompts.forEach(prompt => {
            grouped[prompt.category].push(prompt);
        });

        return grouped;
    }
);
